import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  V2Badge,
  V2DataGrid,
  V2EmptyState,
  LoadingState,
  StatePanel,
  type V2TableColumn,
} from '@sakhtar/design-system/react';
import { HubKpi, HubKpiGrid } from '../../components/hub/HubKpi.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { PartyLink } from './PartyLink.js';
import {
  amountColumnHeader,
  formatRialFa,
  formatRialFaWithUnit,
} from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { faDigits } from '../../lib/faDigits.js';
import { toEnglishDigits } from '../../lib/phoneNumber.js';
import type { CommissionInvoice, CommissionReport } from '../../lib/types/commission.js';

interface Row extends CommissionInvoice {
  readonly id: string;
}

/** Collected share of the invoice as a whole percent, for display only. */
function collectedPercent(row: CommissionInvoice): string {
  const total = BigInt(row.invoiceTotalRial);
  if (total <= 0n) return '۰٪';
  const percent = (BigInt(row.collectedRial) * 100n) / total;
  return `${faDigits(percent.toString())}٪`;
}

/**
 * «فروش من» — D-021. The caller's own posted sales and the commission on
 * them; the server always scopes this to the signed-in salesperson.
 */
export function MySalesPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const { unit } = useAmountDisplay();
  const [query, setQuery] = useState('');
  const report = useApiGet<CommissionReport>(
    slug ? `${base}/api/v1/sales/commission/my-sales` : null,
    [slug],
  );

  if (!slug) return null;
  if (report.status === 'loading') return <LoadingState />;
  if (report.status === 'error') {
    return (
      <StatePanel
        variant={report.error.status === 403 ? 'permission_denied' : 'network_error'}
        title={
          report.error.status === 403
            ? '«فروش من» برای کاربرانی است که نقش فروش دارند'
            : 'فروش شما قابل نمایش نیست'
        }
      />
    );
  }

  const { totals, invoices } = report.data;
  const value = toEnglishDigits(query).trim().toLowerCase();
  const rows = invoices
    .filter(
      (row) =>
        !value ||
        (row.officialNumber ?? '').toLowerCase().includes(value) ||
        row.orderRef.toLowerCase().includes(value) ||
        row.customerName.toLowerCase().includes(value),
    )
    .map((row) => ({ ...row, id: row.invoiceId }));

  const columns: V2TableColumn<Row>[] = [
    {
      key: 'number',
      header: 'شماره فاکتور',
      render: (row) => (
        <Link to={`../invoices/${row.invoiceId}`} className="ltr">
          {row.officialNumber ?? row.orderRef}
        </Link>
      ),
    },
    { key: 'date', header: 'تاریخ', render: (row) => displayJalaliDate(row.businessDate) },
    {
      key: 'customer',
      header: 'مشتری',
      render: (row) => (
        <PartyLink base={base} partyRef={row.customerRef} inRow>
          {row.customerName}
        </PartyLink>
      ),
    },
    {
      key: 'base',
      header: amountColumnHeader('مبلغ فروش', unit),
      align: 'end',
      render: (row) => <span className="sk-num">{formatRialFa(row.baseRial, unit)}</span>,
    },
    {
      key: 'commission',
      header: amountColumnHeader('پورسانت', unit),
      align: 'end',
      render: (row) => <span className="sk-num">{formatRialFa(row.commissionRial, unit)}</span>,
    },
    {
      key: 'collected',
      header: 'وصول‌شده',
      align: 'end',
      render: (row) => <span className="sk-num">{collectedPercent(row)}</span>,
    },
    {
      key: 'released',
      header: amountColumnHeader('پورسانت آزادشده', unit),
      align: 'end',
      render: (row) => <span className="sk-num">{formatRialFa(row.releasedRial, unit)}</span>,
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (row) =>
        row.status === 'reversed' ? (
          <V2Badge tone="neutral">برگشت‌خورده</V2Badge>
        ) : BigInt(row.releasedRial) >= BigInt(row.commissionRial) ? (
          <V2Badge tone="success">کامل آزاد شده</V2Badge>
        ) : (
          <V2Badge tone="warn">در انتظار وصول</V2Badge>
        ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <HubKpiGrid accent="crm">
        <HubKpi
          hero
          label="فروش قطعی من"
          value={formatRialFaWithUnit(totals.baseRial, unit)}
          delta={`${totals.invoiceCount.toLocaleString('fa-IR')} فاکتور`}
          deltaDirection="neutral"
        />
        <HubKpi label="پورسانت من" value={formatRialFaWithUnit(totals.commissionRial, unit)} />
        <HubKpi
          label="آزادشده (وصول‌شده)"
          value={formatRialFaWithUnit(totals.releasedRial, unit)}
        />
        <HubKpi label="در انتظار وصول" value={formatRialFaWithUnit(totals.pendingRial, unit)} />
      </HubKpiGrid>
      <V2DataGrid
        icon="invoice"
        title="فاکتورهای فروش من"
        subtitle="پورسانت با قطعی شدن فاکتور ثبت و به نسبت مبلغ وصول‌شده آزاد می‌شود"
        search={{ value: query, onChange: setQuery, placeholder: 'شماره فاکتور، سفارش یا مشتری' }}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        zebra
        emptyState={
          <V2EmptyState
            icon="invoice"
            title={query ? 'فاکتوری با این جست‌وجو پیدا نشد' : 'هنوز فروش قطعی‌شده‌ای ندارید'}
            description="فاکتورهای قطعی سفارش‌هایی که مسئول آن‌ها هستید، اینجا با پورسانتشان نمایش داده می‌شوند."
          />
        }
      />
    </div>
  );
}
